import React, { useState, useEffect, useRef, useCallback } from 'react';
import { stageAPI } from '../services/api';
import './StageFenetreEnvoi.css';
import { useTailleFenetre } from './fenetreTaille';

/**
 * SUIVI DE STAGE — LA FENÊTRE D'ENVOI « FAÇON OUTLOOK » (lot 3.S-b2)
 * ===================================================================
 * Spec : doc/specs_techniques/courriel_smtp.md §5 (fenêtre), §5 bis SM2/SM3/SM6/SM8/SM9/SM10
 * Maquette : `#certifEnvoi` et `#certifMessage` de doc/maquettes/suivi_de_stage_maquette.html
 *
 * Elle s'ouvre quand le département envoie PAR LE SERVEUR : la route d'envoi a rendu un
 * BROUILLON (rien n'est encore écrit). Tout est visible et corrigeable avant d'envoyer —
 * De et Répondre à (verrouillés), À / Cc / Cci, objet, texte.
 *
 * ⚠️ L'ÉTIQUETTE VERTE NE PORTE PAS L'ADRESSE DU LIEN : c'est le serveur qui la remet au
 * moment d'envoyer (SM3). Effacée, l'envoi est refusé — par le serveur, pas seulement ici.
 *
 * ⚠️ « ENVOYÉ » = ACCEPTÉ PAR LE SERVEUR (SM2). Ce n'est pas « reçu » : la preuve de
 * réception reste « lien ouvert ».
 */

const ADRESSE_RE = /^[^\s@<>"]+@[^\s@<>"]+\.[a-z]{2,}$/i;
const QUI = { etudiant: 'étudiant', maitre: 'maître de stage' };
const externe = (adr) => !/@(etud\.)?univ-evry\.fr$/i.test(adr) && !/@iut\.univ-evry\.fr$/i.test(adr);
const heure = (v) => (v ? new Date(v).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '');
const moment = (v) => (v ? new Date(v).toLocaleDateString('fr-FR') + ' à ' + heure(v) : '');

/** Une étiquette de destinataire : crayon = corriger (dans l'étiquette), × = retirer. */
function Etiquette({ libelle, adresse, corrigee, verrou, onCorriger, onRetirer, titre }) {
    const [edition, setEdition] = useState(null);
    const finir = () => {
        const v = String(edition || '').trim();
        if (!ADRESSE_RE.test(v)) return;            // l'étiquette reste en saisie, en rouge
        onCorriger(v);
        setEdition(null);
    };
    if (verrou) {
        return <span className="fe-chip fe-chip-verrou" title={titre}><i className="fa-solid fa-lock" /> {libelle}</span>;
    }
    if (edition !== null) {
        return (
            <span className={'fe-chip fe-chip-edition' + (ADRESSE_RE.test(edition.trim()) ? '' : ' fe-chip-refus')}>
                {libelle ? <span>{libelle}</span> : null}
                <input autoFocus value={edition} onChange={(e) => setEdition(e.target.value)}
                       onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); finir(); } if (e.key === 'Escape') setEdition(null); }}
                       onBlur={finir} aria-label="Corriger l'adresse" />
            </span>
        );
    }
    return (
        <span className={'fe-chip' + (corrigee ? ' fe-chip-corrigee' : '') + (externe(adresse) ? ' fe-chip-externe' : '')}
              title={externe(adresse) ? 'Adresse extérieure à l\'université — vérifiez-la' : titre}>
            {externe(adresse) && <i className="fa-solid fa-earth-europe" />}
            {libelle ? <span>{libelle}</span> : null}
            <span className="fe-adr">{adresse}</span>
            <i className="fa-solid fa-pen" title="Corriger l'adresse" onClick={() => setEdition(adresse)} />
            <i className="fa-solid fa-xmark" title="Retirer" onClick={onRetirer} />
        </span>
    );
}

/** Le champ « ajouter » d'une ligne, avec l'autocomplétion de l'annuaire du département. */
function Ajout({ placeholder, annuaire, onAjouter }) {
    const [texte, setTexte] = useState('');
    const [ouvert, setOuvert] = useState(false);
    const [refus, setRefus] = useState(false);
    const q = texte.trim().toLowerCase();
    const suggestions = (annuaire || []).filter((p) => !q || `${p.nom} ${p.adresse}`.toLowerCase().includes(q)).slice(0, 6);
    const ajouter = (nom, adresse) => { onAjouter(nom, adresse); setTexte(''); setOuvert(false); setRefus(false); };
    return (
        <div className="fe-ajout">
            <input className={'input-sm' + (refus ? ' fe-refus' : '')} value={texte} placeholder={placeholder}
                   onFocus={() => setOuvert(true)} onBlur={() => setTimeout(() => setOuvert(false), 150)}
                   onChange={(e) => { setTexte(e.target.value); setOuvert(true); setRefus(false); }}
                   onKeyDown={(e) => {
                       if (e.key !== 'Enter') return;
                       e.preventDefault();
                       const v = texte.trim();
                       if (!ADRESSE_RE.test(v)) { setRefus(true); return; }
                       ajouter(null, v);
                   }} />
            {refus && <div className="fe-ajout-refus">Adresse mal formée</div>}
            {ouvert && (
                <div className="fe-suggest">
                    {suggestions.length
                        ? suggestions.map((p) => (
                            <div key={p.adresse} onMouseDown={() => ajouter(p.nom, p.adresse)}>
                                {p.nom} <small>{p.role} · {p.adresse}</small>
                            </div>))
                        : <div className="fe-suggest-vide">Aucune suggestion — tapez l'adresse complète puis Entrée</div>}
                </div>
            )}
        </div>
    );
}

/** Les bandeaux d'environnement : serveur de test (redirection), poste (Mailpit). */
function Bandeaux({ environnement }) {
    if (!environnement) return null;
    return (
        <>
            {environnement.redirection_imposee && (
                <div className="fe-bande fe-bande-test">
                    <i className="fa-solid fa-flask" />
                    <span>
                        {environnement.redirection_adresse
                            ? <><b>Serveur de test — les destinataires extérieurs sont remplacés par {environnement.redirection_adresse}</b>, avec en tête
                                « Aurait été envoyé à : … ». Les <b>comptes du département</b> (vous-même, assistantes, responsables) reçoivent
                                pour de vrai, en À, Cc ou Cci ; toutes les autres adresses partent à l'adresse de test. Les adresses ci-dessous sont celles qu'utiliserait la production : vérifiez-les.</>
                            : <><b>Serveur de test — aucune adresse de redirection n'est réglée : rien ne partira.</b> Administration → Configuration.</>}
                    </span>
                </div>
            )}
            {environnement.serveur_local && (
                <div className="fe-bande fe-bande-local">
                    <i className="fa-solid fa-laptop-code" />
                    <span>Poste de développement : le message part dans Mailpit (http://localhost:8025), rien ne sort.</span>
                </div>
            )}
        </>
    );
}

/**
 * LA FENÊTRE. `brouillon` = la réponse d'une route d'envoi en mode serveur.
 * `onTermine(true)` quand quelque chose a été fait (envoyé, préparé en secours) : le
 * parent relit le tableau et le panneau.
 */
/**
 * `api` (lot C.4a) : la Certification fournit SES appels (`envoyer`, `essai`) — une seule fenêtre pour
 * tout le module (C32). Sans `api`, ce sont ceux du Stage, inchangés.
 */
/**
 * Lot 3.5b-3 — une SÉRIE de messages (« Prévenir les tuteurs » après une répartition) : la même
 * fenêtre, ouverte message après message. `serie = { position, total, libelle, onSuivant(etat), onArreter }`.
 */
export function BandeauSerie({ serie }) {
    return (
        <div className="stg-encart stg-encart-info fe-serie">
            <i className="fa-solid fa-list-ol" />
            <span>Message <b>{serie.position}</b> sur <b>{serie.total}</b>{serie.libelle ? <> — {serie.libelle}</> : null}</span>
            <button type="button" className="btn btn-outline btn-sm" onClick={serie.onArreter}>Arrêter la série</button>
        </div>
    );
}
/**
 * Lot 3.5d-3 — points d'accroche de la transmission au CFA (sans eux, rien ne change) :
 * `extension = { avantObjet, apresObjet, texteRef, bloquer(), textes: { essai, secours, bilan, journal } }`.
 * `avantObjet` / `apresObjet` : des lignes de plus (Alternants, Pièce jointe) ; `texteRef` : la zone du texte, pour y
 * remplacer le tableau protégé ; `bloquer()` : la raison qui empêche d'envoyer (« Cochez au moins un alternant »).
 */
const libelleSuivant = (serie) => (serie.position < serie.total ? <>Message suivant <i className="fa-solid fa-chevron-right" /></> : 'Terminer la série');

function StageFenetreEnvoi({ brouillon, departementId, onFermer, onTermine, api = null, serie = null, extension = null, onModeles = null }) {
    const [a, setA] = useState(() => (brouillon.a || []).map((x) => ({ ...x, origine: x.adresse, prochains: false })));
    const [cc, setCc] = useState(() => (brouillon.cc || []).map((c) => ({ ...c })));
    const [cci, setCci] = useState([]);
    const [sujet, setSujet] = useState(brouillon.sujet || '');
    const [annuaire, setAnnuaire] = useState([]);
    const [occupe, setOccupe] = useState(false);
    const [erreur, setErreur] = useState(null);
    const [retour, setRetour] = useState(null);         // essai ou mailto de secours : la fenêtre reste ouverte
    const [bilan, setBilan] = useState(null);           // après « Envoyer »
    const [menuEssai, setMenuEssai] = useState(false);
    const [adresseEssai, setAdresseEssai] = useState(brouillon.cci_auto || '');
    const [fait, setFait] = useState(false);
    const taille = useTailleFenetre();          // 2026-10-10 : redimensionnable, taille mémorisée (compte + poste)
    const texte = useRef(null);
    /**
     * ⚠️ LE TEXTE VIT DANS UNE RÉFÉRENCE, PAS DANS LA ZONE AFFICHÉE. Le bilan d'un échec
     * remplace la fenêtre ; « Revenir au message » remonte une zone neuve. Sans cette
     * mémoire, le texte corrigé repartirait du modèle. Et React ne réécrit JAMAIS la zone
     * pendant la frappe : elle n'est remplie qu'à son montage.
     */
    const htmlRef = useRef(brouillon.html || '');
    const poserTexte = useCallback((el) => {
        texte.current = el;
        if (extension?.texteRef) extension.texteRef.current = el;
        if (el && el.innerHTML !== htmlRef.current) el.innerHTML = htmlRef.current;
    }, []);
    const htmlCourant = () => (texte.current ? (htmlRef.current = texte.current.innerHTML) : htmlRef.current);
    /*
     * « Modèles de courriel… » (lot MC, 2026-10-28 — le même geste que la fenêtre groupée) : l'éditeur du modèle de CET
     * envoi ; enregistré, le texte de la fenêtre suit — sauf s'il a été retouché : on demande. « Retouché » = un repère
     * posé à la frappe (le navigateur réécrit le HTML : une comparaison au texte reçu ne serait jamais « identique »).
     */
    const retoucheRef = useRef(false);
    const [versionTexte, setVersionTexte] = useState(0);
    const [remplacement, setRemplacement] = useState(null);
    const poserTexteModele = (t) => { retoucheRef.current = false; htmlRef.current = t.html; setSujet(t.sujet); setVersionTexte((v) => v + 1); };
    const ouvrirModeles = async () => {
        const t = await onModeles(brouillon.type_mail);
        if (!t) return;
        if (retoucheRef.current) setRemplacement(t); else poserTexteModele(t);
    };
    useEffect(() => {
        stageAPI.annuaireEnvoi(departementId).then((r) => setAnnuaire(r.data.personnes || [])).catch(() => setAnnuaire([]));
    }, [departementId]);

    const edition = useCallback(() => ({
        a: a.map((x) => ({ adresse: x.adresse, qui: x.qui || null, prochains: !!x.prochains })),
        cc: cc.map((x) => x.adresse),
        cci: cci.map((x) => x.adresse),
        sujet,
        html: htmlCourant()
    }), [a, cc, cci, sujet]);

    const corps = (extra = {}) => ({ type: brouillon.type, dossier_id: brouillon.dossier_id, edition: edition(), ...extra });
    const lireErreur = (e) => ({ message: e.response?.data?.error || 'L\'opération a échoué', detail: e.response?.data?.detail || null });

    const verifier = () => {
        const bloque = extension?.bloquer ? extension.bloquer() : null;
        if (bloque) return bloque;
        if (!a.length) return 'Ajoutez au moins un destinataire.';
        if (!sujet.trim()) return "L'objet est obligatoire.";
        // Lot 3.5d-0 : un message LIBRE (entreprises) part d'un texte vide — il ne part pas vide.
        if (!htmlCourant().replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim()) return 'Le texte est vide : écrivez votre message.';
        if (brouillon.avec_lien && !/class="[^"]*envoi-lien/.test(htmlCourant())) {
            return "Le lien personnel a été effacé : l'envoi est bloqué. Fermez la fenêtre et rouvrez-la pour repartir du modèle.";
        }
        return null;
    };

    const envoyer = async () => {
        const v = verifier();
        if (v) { setErreur({ message: v }); return; }
        setOccupe(true); setErreur(null); setRetour(null);
        try {
            const r = brouillon.portee === 'diffusion'
                ? await stageAPI.diffusionPromotion(departementId, { configuration_id: brouillon.configuration_id, edition: edition() })
                : await (api?.envoyer || stageAPI.envoyerServeur)(departementId, corps());
            setBilan(r.data);
            if (r.data.ok) setFait(true);
        } catch (e) { setErreur(lireErreur(e)); }
        finally { setOccupe(false); }
    };

    const essayer = async () => {
        setMenuEssai(false);
        const v = verifier();
        if (v) { setErreur({ message: v }); return; }
        if (!ADRESSE_RE.test(adresseEssai.trim())) { setErreur({ message: "L'adresse d'essai n'est pas valide." }); return; }
        setOccupe(true); setErreur(null);
        try {
            const r = await (api?.essai || stageAPI.essaiServeur)(departementId, corps({ adresse_essai: adresseEssai.trim() }));
            setRetour(r.data.ok
                ? { ton: 'ok', texte: extension?.textes?.essai ? extension.textes.essai(r.data) : <>Essai envoyé à <b>{r.data.adresse}</b>{r.data.redirige_vers ? ` (redirigé vers ${r.data.redirige_vers})` : ''} : objet « [ESSAI] {sujet} », bandeau « aurait été envoyé à » en tête, lien vers l'aperçu. <b>Rien n'est enregistré dans le dossier</b> — vérifiez votre boîte, puis envoyez pour de bon.</> }
                : { ton: 'ko', texte: <>L'essai a échoué : « {r.data.erreur} »</> });
        } catch (e) { setErreur(lireErreur(e)); }
        finally { setOccupe(false); }
    };

    const secours = async () => {
        const v = verifier();
        if (v) { setErreur({ message: v }); return; }
        setOccupe(true); setErreur(null);
        try {
            const r = brouillon.portee === 'diffusion'
                ? await stageAPI.preparerLienPromotion(departementId, brouillon.configuration_id, 'mailto')
                : await (api?.mailto || stageAPI.mailtoServeur)(departementId, corps());
            setFait(true);
            setRetour({ ton: 'ok', texte: extension?.textes?.secours ? extension.textes.secours(r.data) : <>Message <b>préparé</b> dans votre messagerie (mode secours) : destinataires, objet et texte repris d'ici, le lien en adresse brute. Le journal note « préparé », pas « envoyé ».{r.data.trop_long ? ' ⚠️ Le message est très long : votre messagerie pourrait le couper.' : ''}</> });
            window.location.href = r.data.mailto;
        } catch (e) { setErreur(lireErreur(e)); }
        finally { setOccupe(false); }
    };

    const fermer = () => { if (occupe) return; if (fait) onTermine(true); if (serie) serie.onArreter(); else onFermer(); };
    // Série : passer ou continuer — ce qui est parti est signalé à l'appelant comme d'habitude.
    const suivant = (etat) => { if (occupe) return; if (fait) onTermine(true); serie.onSuivant(etat); };

    const titre = `${brouillon.titre || 'Envoyer un message'}${brouillon.etudiant ? ' — ' + brouillon.etudiant : ''}`;

    // ── Le bilan, après « Envoyer » ──
    if (bilan) {
        const reels = bilan.destinataires || {};
        return (
            <div className="stg-modale-fond" role="dialog" aria-modal="true" aria-label={titre} onClick={fermer}>
                <div className={'stg-modale fe-fenetre' + taille.classe} style={taille.style} onClick={(e) => e.stopPropagation()}>
                    {taille.poignee}
                    <div className="stg-modale-header fe-titre"><i className="fa-solid fa-paper-plane" /><span>{titre}</span></div>
                    <div className="stg-modale-body">
                        <Bandeaux environnement={brouillon.environnement} />{serie && <BandeauSerie serie={serie} />}
                        {bilan.ok ? (
                            <div className="fe-bilan fe-bilan-ok">
                                <i className="fa-solid fa-circle-check" />
                                <div>
                                    <b>Message envoyé</b> — accepté par le serveur à {heure(bilan.envoye_le)}.
                                    <div className="fe-bilan-detail">
                                        À : {(reels.a || []).join(', ')}
                                        {(reels.cc || []).length ? <> · Cc : {reels.cc.join(', ')}</> : null}
                                        {(reels.cci || []).length ? <> · Cci : {reels.cci.join(', ')}</> : null}
                                    </div>
                                    {bilan.redirige_vers && <div className="fe-bilan-detail"><i className="fa-solid fa-flask" /> Serveur de test : parti à {bilan.redirige_vers}.</div>}
                                    {(bilan.adresses_corrigees || []).map((c) => (
                                        <div key={c.qui} className="fe-bilan-detail"><i className="fa-solid fa-pen" /> Prochains envois à {QUI[c.qui]} : {c.apres} (au lieu de {c.avant}).</div>
                                    ))}
                                    {bilan.grille_vient_de_figer && <div className="fe-bilan-detail"><i className="fa-solid fa-lock" /> La grille d'évaluation de la campagne vient d'être figée.</div>}
                                    {extension?.textes?.bilan && <div className="fe-bilan-detail">{extension.textes.bilan(bilan)}</div>}
                                    <div className="fe-bilan-detail">Le message est au journal{extension ? '' : ' du dossier'}, tel qu'envoyé. « Envoyé » veut dire accepté par le serveur ; la preuve de réception reste « lien ouvert ».</div>
                                </div>
                            </div>
                        ) : (
                            <div className="fe-bilan fe-bilan-ko">
                                <i className="fa-solid fa-triangle-exclamation" />
                                <div>
                                    <b>Échec de l'envoi</b> — refusé par le serveur : « {bilan.erreur} ».
                                    <div className="fe-bilan-detail">Le dossier n'a pas avancé. L'échec est noté au journal et signalé à l'administrateur (Administration → Envoi des courriels).</div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="stg-modale-pied">
                        {!bilan.ok && (
                            <>
                                {brouillon.mailto_secours && (
                                    <button type="button" className="btn btn-outline btn-sm" onClick={() => { setBilan(null); secours(); }} disabled={occupe}>
                                        <i className="fa-solid fa-envelope-open" /> Ouvrir dans ma messagerie
                                    </button>
                                )}
                                <button type="button" className="btn btn-outline btn-sm" onClick={() => setBilan(null)}>
                                    <i className="fa-solid fa-pen" /> Revenir au message
                                </button>
                                <button type="button" className="btn btn-sm btn-danger" onClick={envoyer} disabled={occupe}>
                                    <i className="fa-solid fa-rotate-right" /> Renvoyer
                                </button>
                            </>
                        )}
                        {serie
                            ? (bilan.ok
                                ? <button type="button" className="btn btn-green btn-sm" onClick={() => suivant('envoye')} disabled={occupe}>{libelleSuivant(serie)}</button>
                                : <button type="button" className="btn btn-outline btn-sm" onClick={() => suivant('echec')} disabled={occupe}>Passer</button>)
                            : <button type="button" className="btn btn-green btn-sm" onClick={fermer} disabled={occupe}>Fermer</button>}
                    </div>
                </div>
            </div>
        );
    }

    const ligneA = (x, i) => (
        <Etiquette key={'a' + i} libelle={[x.nom, x.qui && QUI[x.qui]].filter(Boolean).join(' · ') || null}
                   adresse={x.adresse} corrigee={x.origine && x.adresse !== x.origine}
                   onCorriger={(v) => setA((l) => l.map((y, k) => (k === i ? { ...y, adresse: v } : y)))}
                   onRetirer={() => setA((l) => l.filter((_, k) => k !== i))} />
    );
    const corrigees = a.map((x, i) => ({ ...x, i })).filter((x) => x.qui && x.origine && x.adresse !== x.origine);

    return (
        <div className="stg-modale-fond" role="dialog" aria-modal="true" aria-label={titre} onClick={fermer}>
            <div className={'stg-modale fe-fenetre' + taille.classe} style={taille.style} onClick={(e) => e.stopPropagation()}>
                {taille.poignee}
                <div className="stg-modale-header fe-titre"><i className="fa-solid fa-paper-plane" /><span>{titre}</span></div>
                <div className="stg-modale-body fe-corps">
                    <Bandeaux environnement={brouillon.environnement} />{serie && <BandeauSerie serie={serie} />}
                    {brouillon.lien_renouvele && (
                        <div className="stg-encart stg-encart-alerte" style={{ marginBottom: 10 }}>
                            <i className="fa-solid fa-rotate" />
                            <span>Un <b>nouveau lien</b> remplace le précédent, qui <b>ne fonctionne plus</b> : c'est celui de ce message qui comptera.</span>
                        </div>
                    )}

                    <div className="fe-ligne">
                        <label>De</label>
                        <div className="fe-fixe"><i className="fa-solid fa-lock" title="L'expéditeur est toujours la boîte du département" /> {brouillon.de?.nom} &lt;{brouillon.de?.adresse}&gt;</div>
                    </div>
                    <div className="fe-ligne">
                        <label>Répondre&nbsp;à</label>
                        <div className="fe-fixe"><i className="fa-solid fa-lock" title="Les réponses arrivent dans votre boîte" /> {brouillon.repondre_a}</div>
                    </div>

                    <div className="fe-ligne">
                        <label>À</label>
                        <div className="fe-champ">
                            <div className="fe-chips">
                                {a.map(ligneA)}
                                <Ajout placeholder="Ajouter un destinataire…" annuaire={annuaire}
                                       onAjouter={(nom, adresse) => setA((l) => [...l, { adresse, nom, qui: null, origine: null }])} />
                            </div>
                            {corrigees.map((x) => (
                                <label key={'p' + x.i} className="fe-prochains">
                                    <input type="checkbox" checked={!!x.prochains}
                                           onChange={(e) => setA((l) => l.map((y, k) => (k === x.i ? { ...y, prochains: e.target.checked } : y)))} />
                                    Utiliser {x.adresse} pour les prochains envois de ce dossier ({QUI[x.qui]})
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="fe-ligne">
                        <label>Cc</label>
                        <div className="fe-chips fe-champ">
                            {cc.map((c, i) => (
                                <Etiquette key={'c' + i} libelle={c.nom ? `${c.nom}${c.role ? ' · ' + c.role : ''}` : null} adresse={c.adresse}
                                           titre={c.role ? `Case « Cc ${c.role}s » du modèle` : undefined}
                                           onCorriger={(v) => setCc((l) => l.map((y, k) => (k === i ? { ...y, adresse: v } : y)))}
                                           onRetirer={() => setCc((l) => l.filter((_, k) => k !== i))} />
                            ))}
                            <Ajout placeholder="Ajouter en copie…" annuaire={annuaire} onAjouter={(nom, adresse) => setCc((l) => [...l, { nom, adresse }])} />
                        </div>
                    </div>

                    <div className="fe-ligne">
                        <label>Cci</label>
                        <div className="fe-chips fe-champ">
                            {brouillon.cci_auto && (
                                <Etiquette verrou libelle={`vous-même (${brouillon.cci_auto})`}
                                           titre="Votre copie : Partage ne range rien dans « Envoyé ». Non retirable." />
                            )}
                            {cci.map((c, i) => (
                                <Etiquette key={'b' + i} libelle={c.nom} adresse={c.adresse}
                                           onCorriger={(v) => setCci((l) => l.map((y, k) => (k === i ? { ...y, adresse: v } : y)))}
                                           onRetirer={() => setCci((l) => l.filter((_, k) => k !== i))} />
                            ))}
                            <Ajout placeholder="Ajouter en copie cachée…" annuaire={annuaire} onAjouter={(nom, adresse) => setCci((l) => [...l, { nom, adresse }])} />
                        </div>
                    </div>

                    {extension?.avantObjet}
                    <div className="fe-ligne">
                        <label>Objet</label>
                        <input className="input-sm fe-champ" value={sujet} maxLength={300} onChange={(e) => { retoucheRef.current = true; setSujet(e.target.value); }} aria-label="Objet" />
                        {onModeles && (
                            <button type="button" className="btn btn-outline btn-sm fe-modeles" onClick={ouvrirModeles}
                                    title="Ouvre le modèle de courriel de cet envoi (versions, texte d'origine). Enregistré, il remplace le texte de cette fenêtre — on vous demande si vous l'aviez retouché.">
                                <i className="fa-solid fa-pen" /> Modèles de courriel…
                            </button>
                        )}
                    </div>
                    {remplacement && (
                        <div className="fe-garde">
                            <i className="fa-solid fa-triangle-exclamation" />
                            <div style={{ flex: 1 }}><b>Le modèle de courriel a changé.</b> Vous aviez retouché le texte de cette fenêtre : lequel garder pour cet envoi ?</div>
                            <button type="button" className="btn btn-outline btn-sm" onClick={() => { poserTexteModele(remplacement); setRemplacement(null); }}>Prendre le nouveau modèle</button>
                            <button type="button" className="btn btn-outline btn-sm" onClick={() => setRemplacement(null)}>Garder mon texte</button>
                        </div>
                    )}
                    {extension?.apresObjet}

                    <div className="fe-barre" role="toolbar" aria-label="Mise en forme">
                        <button type="button" title="Gras" onMouseDown={(e) => { e.preventDefault(); document.execCommand('bold'); }}><b>G</b></button>
                        <button type="button" title="Italique" onMouseDown={(e) => { e.preventDefault(); document.execCommand('italic'); }}><i>I</i></button>
                        <button type="button" title="Liste à puces" onMouseDown={(e) => { e.preventDefault(); document.execCommand('insertUnorderedList'); }}><i className="fa-solid fa-list-ul" /></button>
                    </div>
                    <div key={versionTexte} className="fe-texte" contentEditable suppressContentEditableWarning ref={poserTexte}
                         onInput={(e) => { retoucheRef.current = true; htmlRef.current = e.currentTarget.innerHTML; }} aria-label="Texte du message" />
                    <div className="fe-aide">
                        <i className="fa-solid fa-circle-info" /> Vos modifications valent <b>pour cet envoi seulement</b> : le modèle ne change pas.
                        {brouillon.avec_lien && <> L'étiquette verte est le lien personnel — le serveur y pose la vraie adresse ; si elle est effacée, l'envoi est bloqué.</>}
                    </div>

                    {retour && (
                        <div className={'fe-retour ' + (retour.ton === 'ok' ? 'fe-retour-ok' : 'fe-retour-ko')}>
                            <i className={'fa-solid ' + (retour.ton === 'ok' ? 'fa-flask' : 'fa-circle-xmark')} /><span>{retour.texte}</span>
                        </div>
                    )}
                    {erreur && (
                        <div className="fe-retour fe-retour-ko">
                            <i className="fa-solid fa-circle-xmark" />
                            <span><b>{erreur.message}</b>{erreur.detail ? <> {erreur.detail}</> : null}</span>
                        </div>
                    )}
                </div>

                <div className="stg-modale-pied fe-pied">
                    <span className="fe-journal"><i className="fa-solid fa-book" /> {extension?.textes?.journal || "Le journal gardera ce message tel qu'envoyé."}</span>
                    {serie
                        ? (fait
                            ? <button type="button" className="btn btn-green btn-sm" onClick={() => suivant('prepare')} disabled={occupe}>{libelleSuivant(serie)}</button>
                            : <button type="button" className="btn btn-outline btn-sm" onClick={() => suivant('passe')} disabled={occupe}
                                      title="Ne pas envoyer ce message et passer au suivant">Passer</button>)
                        : <button type="button" className="btn btn-outline btn-sm" onClick={fermer} disabled={occupe}>{fait ? 'Fermer' : 'Annuler'}</button>}
                    {brouillon.mailto_secours && (
                        <button type="button" className="btn btn-outline btn-sm" onClick={secours} disabled={occupe}
                                title="Si le serveur est en panne : ouvre votre messagerie avec les destinataires, l'objet et le texte corrigés ici. L'écran dira « préparé » ; le lien apparaîtra en adresse brute.">
                            <i className="fa-solid fa-envelope-open" /> Ouvrir dans ma messagerie
                        </button>
                    )}
                    {brouillon.portee !== 'diffusion' && (
                    <div className="fe-essai">
                        <button type="button" className="btn btn-outline btn-sm" onClick={() => setMenuEssai((o) => !o)} disabled={occupe}>
                            <i className="fa-solid fa-flask" /> M'envoyer un essai <i className="fa-solid fa-caret-down" />
                        </button>
                        {menuEssai && (
                            <div className="fe-essai-menu">
                                <div className="fe-essai-aide">
                                    Tout part à l'adresse d'essai — <b>personne d'autre</b>, ni les copies. Objet « [ESSAI] », bandeau « aurait été envoyé à », lien vers l'aperçu. <b>Rien n'est enregistré</b> dans le dossier.
                                </div>
                                <label>Adresse d'essai</label>
                                <input className="input-sm" value={adresseEssai} onChange={(e) => setAdresseEssai(e.target.value)} />
                                <button type="button" className="fe-essai-opt" onClick={essayer}><i className="fa-solid fa-envelope" /> Le message affiché (1)</button>
                            </div>
                        )}
                    </div>
                    )}
                    <button type="button" className="btn btn-green btn-sm" onClick={envoyer} disabled={occupe || fait || !!(extension?.bloquer && extension.bloquer())}
                            title={extension?.bloquer ? (extension.bloquer() || undefined) : undefined}>
                        {occupe ? <><i className="fa-solid fa-circle-notch stg-tourne" /> Envoi…</> : <><i className="fa-solid fa-paper-plane" /> Envoyer 1 message</>}
                    </button>
                </div>
            </div>
        </div>
    );
}

/** « Voir le message envoyé » — le message RÉEL, tel qu'il est parti (maquette `#certifMessage`). */
export function StageMessageEnvoye({ envoiId, departementId, onFermer }) {
    const taille = useTailleFenetre();
    const [m, setM] = useState(null);
    const [erreur, setErreur] = useState(null);
    useEffect(() => {
        stageAPI.messageEnvoye(envoiId, departementId).then((r) => setM(r.data))
            .catch((e) => setErreur(e.response?.data?.error || 'Lecture du message impossible'));
    }, [envoiId, departementId]);
    const d = m?.destinataires_reels || {};
    const dem = d.demandes || {}, reels = d.reels || {};
    const liste = (x) => (x && x.length ? x.join(', ') : '—');
    return (
        <div className="stg-modale-fond" role="dialog" aria-modal="true" aria-label="Message envoyé" onClick={onFermer}>
            <div className={'stg-modale fe-fenetre' + taille.classe} style={taille.style} onClick={(e) => e.stopPropagation()}>
                {taille.poignee}
                <div className="stg-modale-header fe-titre"><i className="fa-solid fa-envelope-open-text" />
                    <span>Message {m?.mode === 'mailto' ? 'préparé' : 'envoyé'}{m ? ' — ' + moment(m.envoye_le || m.prepare_le) : ''}</span>
                </div>
                <div className="stg-modale-body">
                    {erreur && <div className="fe-retour fe-retour-ko"><i className="fa-solid fa-circle-xmark" /><span>{erreur}</span></div>}
                    {!m && !erreur && <div><i className="fa-solid fa-circle-notch stg-tourne" /> Lecture…</div>}
                    {m && (
                        <>
                            <div className="fe-entetes">
                                {d.de && <div><b>De :</b> {d.de.nom} &lt;{d.de.adresse}&gt;</div>}
                                {d.repondre_a && <div><b>Répondre à :</b> {d.repondre_a}</div>}
                                <div><b>À :</b> {liste(dem.a || (m.destinataire ? [m.destinataire] : []))}</div>
                                {(dem.cc || []).length > 0 && <div><b>Cc :</b> {liste(dem.cc)}</div>}
                                {(dem.cci || []).length > 0 && <div><b>Cci :</b> {liste(dem.cci)}</div>}
                                {m.redirige_vers && <div><b>Serveur de test :</b> parti en réalité à {m.redirige_vers} ({liste(reels.a)})</div>}
                                <div><b>Objet :</b> {m.sujet}</div>
                                <div><b>Résultat :</b> {m.resultat === 'envoye'
                                    ? <span className="fe-ok"><i className="fa-solid fa-check" /> accepté par le serveur</span>
                                    : m.resultat === 'echec'
                                        ? <span className="fe-ko"><i className="fa-solid fa-xmark" /> refusé : « {m.erreur} »</span>
                                        : <span>préparé dans la messagerie (mode {m.mode})</span>}
                                    {m.prepare_par_nom ? <> · par {m.prepare_par_nom}</> : null}
                                </div>
                            </div>
                            {/* ⚠️ Le texte est rendu dans un cadre ISOLÉ (sandbox) : même nettoyé par le serveur,
                                un message n'a rien à exécuter dans l'application. */}
                            {m.mode === 'mailto'
                                ? <pre className="fe-texte-brut">{m.texte_envoye || '(texte non conservé)'}</pre>
                                : <iframe title="Texte envoyé" className="fe-cadre" sandbox="" srcDoc={m.texte_envoye || ''} />}
                        </>
                    )}
                </div>
                <div className="stg-modale-pied"><button type="button" className="btn btn-outline btn-sm" onClick={onFermer}>Fermer</button></div>
            </div>
        </div>
    );
}

// Lot 3.S-c : la fenêtre collective réutilise les mêmes briques.
export { Etiquette, Ajout, Bandeaux, ADRESSE_RE };
export default StageFenetreEnvoi;
